import { useEffect, useId, useState, useSyncExternalStore } from "react";
import {
  FEEDBOT_MARK_B_PATH,
  FEEDBOT_MARK_BUBBLE_PATH,
  FEEDBOT_MARK_F_POINTS,
  FEEDBOT_MARK_VIEWBOX,
} from "./FeedBotMark.js";
import { motion, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";

/**
 * Centerline of the bubble ring, starting at the tail tip. Stroked wide inside a
 * mask so the original filled outline appears to draw itself.
 */
const BUBBLE_TRACE_PATH =
  "M10 440 L63.2 382.5 A235 235 0 0 1 460 129.5 A235 235 0 0 1 63.2 382.5";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const EASE_IN_OUT = [0.65, 0, 0.35, 1] as const;

const GLOW_KEYFRAMES = [
  "drop-shadow(0 0 0px rgba(85, 188, 166, 0))",
  "drop-shadow(0 0 10px rgba(85, 188, 166, 0.38))",
  "drop-shadow(0 0 0px rgba(85, 188, 166, 0))",
];

/**
 * How long the entrance owns the mark. The glow ends at 1.2s; after this the
 * complete static mark replaces it even if animation frames were throttled.
 */
export const FEEDBOT_MARK_ENTRANCE_MS = 1_300;

/** Soft fade of the finished mark before a replay restarts from an empty canvas. */
export const FEEDBOT_MARK_REPLAY_FADE_MS = 280;

type MarkPhase = "entrance" | "rest" | "fading";

interface MarkState {
  phase: MarkPhase;
  cycle: number;
}

function nextMarkState(state: MarkState): MarkState {
  switch (state.phase) {
    case "entrance":
      return { ...state, phase: "rest" };
    case "rest":
      return { ...state, phase: "fading" };
    case "fading":
      return { phase: "entrance", cycle: state.cycle + 1 };
    default: {
      const unreachable: never = state.phase;
      return unreachable;
    }
  }
}

/** Time spent in a phase; `null` rests indefinitely. Sums to the replay interval. */
function markPhaseMs(phase: MarkPhase, replayIntervalMs: number | undefined): number | null {
  switch (phase) {
    case "entrance":
      return FEEDBOT_MARK_ENTRANCE_MS;
    case "fading":
      return FEEDBOT_MARK_REPLAY_FADE_MS;
    case "rest":
      return replayIntervalMs
        ? Math.max(0, replayIntervalMs - FEEDBOT_MARK_ENTRANCE_MS - FEEDBOT_MARK_REPLAY_FADE_MS)
        : null;
    default: {
      const unreachable: never = phase;
      return unreachable;
    }
  }
}

function toRest(state: MarkState): MarkState {
  return state.phase === "rest" ? state : { ...state, phase: "rest" };
}

function subscribeToVisibility(onChange: () => void): () => void {
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}

function isDocumentVisible(): boolean {
  return document.visibilityState !== "hidden";
}

function isServerVisible(): boolean {
  return true;
}

interface EntranceSequenceProps {
  label?: string;
  /** When false, renders the complete mark with no animated values at all. */
  playing: boolean;
}

function EntranceSequence({ label, playing }: EntranceSequenceProps): React.JSX.Element {
  const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const bubbleMaskId = `${baseId}-bubble`;
  const fMaskId = `${baseId}-f`;
  const bMaskId = `${baseId}-b`;

  const letterReveal = (delay: number) =>
    playing
      ? {
          initial: { scaleY: 0 },
          animate: { scaleY: 1 },
          transition: { delay, duration: 0.32, ease: EASE_OUT },
        }
      : { initial: false as const };

  const letterFade = (delay: number) =>
    playing
      ? {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { delay, duration: 0.28, ease: EASE_OUT },
        }
      : { initial: false as const };

  return (
    <motion.span
      className="block size-full"
      {...(playing
        ? {
            animate: { filter: GLOW_KEYFRAMES },
            transition: { delay: 0.86, duration: 0.34, times: [0, 0.45, 1], ease: "easeInOut" },
          }
        : {})}
    >
      <svg
        viewBox={FEEDBOT_MARK_VIEWBOX}
        role={label ? "img" : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        focusable="false"
        className="block size-full fill-brand"
      >
        <defs>
          <mask id={bubbleMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="512" height="512">
            <motion.path
              d={BUBBLE_TRACE_PATH}
              fill="none"
              stroke="white"
              strokeWidth={96}
              strokeLinecap="round"
              strokeLinejoin="round"
              {...(playing
                ? {
                    initial: { pathLength: 0, opacity: 0 },
                    animate: { pathLength: 1, opacity: 1 },
                    transition: {
                      pathLength: { delay: 0.05, duration: 0.55, ease: EASE_IN_OUT },
                      opacity: { delay: 0.05, duration: 0.06 },
                    },
                  }
                : { initial: false as const })}
            />
          </mask>
          <mask id={fMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="512" height="512">
            <motion.rect
              x="160"
              y="150"
              width="142"
              height="172"
              fill="white"
              style={{ originY: 1 }}
              {...letterReveal(0.5)}
            />
          </mask>
          <mask id={bMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="512" height="512">
            <motion.rect
              x="238"
              y="187"
              width="144"
              height="172"
              fill="white"
              style={{ originY: 1 }}
              {...letterReveal(0.62)}
            />
          </mask>
        </defs>

        <path fillRule="evenodd" d={FEEDBOT_MARK_BUBBLE_PATH} mask={`url(#${bubbleMaskId})`} />
        <motion.polygon
          points={FEEDBOT_MARK_F_POINTS}
          mask={`url(#${fMaskId})`}
          {...letterFade(0.5)}
        />
        <motion.path d={FEEDBOT_MARK_B_PATH} mask={`url(#${bMaskId})`} {...letterFade(0.62)} />
      </svg>
    </motion.span>
  );
}

interface FeedBotAnimatedMarkProps {
  /** Accessible name. Omit when the mark is decorative. */
  label?: string;
  /** Size and spacing classes; the mark fills this box. */
  className?: string;
  /** Replays the full entrance at this interval, measured between sequence starts. */
  replayIntervalMs?: number;
}

/**
 * FB-in-bubble mark with an entrance: bubble outline draws, then F, then B,
 * then a brief mint glow. Between replays, under reduced motion, and while the
 * tab is hidden it renders the complete static mark, so no unfinished animation
 * can leave it invisible.
 */
export function FeedBotAnimatedMark({
  label,
  className,
  replayIntervalMs,
}: FeedBotAnimatedMarkProps): React.JSX.Element {
  const reduceMotion = useReducedMotion() ?? false;
  const visible = useSyncExternalStore(subscribeToVisibility, isDocumentVisible, isServerVisible);
  const animated = !reduceMotion && visible;
  const [state, setState] = useState<MarkState>({ phase: "entrance", cycle: 0 });
  const phase = animated ? state.phase : "rest";

  useEffect(() => {
    if (!animated) {
      setState(toRest);
      return;
    }
    const delay = markPhaseMs(state.phase, replayIntervalMs);
    if (delay === null) return;
    const timer = window.setTimeout(() => setState(nextMarkState), delay);
    return () => window.clearTimeout(timer);
  }, [animated, state.phase, state.cycle, replayIntervalMs]);

  return (
    <span
      className={cn(
        "inline-block",
        phase === "fading"
          ? "opacity-0 transition-opacity duration-280 ease-[cubic-bezier(0,0,0.58,1)]"
          : "opacity-100",
        className,
      )}
    >
      {phase === "entrance" ? (
        <EntranceSequence key={`entrance-${state.cycle}`} label={label} playing />
      ) : (
        <EntranceSequence key="rest" label={label} playing={false} />
      )}
    </span>
  );
}
